import type {
  DirectionSegment,
  IngredientRef,
  ScaledQuantity,
  Temperature,
  VesselRef,
} from './types';
import { MAIN_VESSEL } from './vessels';

const PLACEHOLDER_RE = /\{([^}]*)\}/g;
const TEMP_RE = /^(-?\d+(?:\.\d+)?)([FC])$/;
const QTY_RE = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(?:\s+(.+))?$/;

/**
 * Parse a direction bullet template string into segments.
 *
 * Placeholders inside `{...}`:
 *   {name}            → IngredientRef (ref by ingredient name)
 *   {name:half}       → IngredientRef with portion 'half'
 *   {name:third}      → IngredientRef with portion 'third'
 *   {name:qty unit}   → IngredientRef with explicit { quantity, unit } portion
 *                       e.g. {flour:1/2 cup}
 *   {=qty unit}       → standalone ScaledQuantity (no ingredient ref)
 *                       e.g. {=1/2 cup}
 *   {=fixed qty unit} → ScaledQuantity that does NOT scale with recipe
 *                       multiplier but still unit-converts (e.g. batch
 *                       sizes like {=fixed 1 lb} batches)
 *   {!350F} / {!350C} → Temperature in Fahrenheit / Celsius (one form)
 *   {vessel}          → VesselRef for the MAIN_VESSEL entry of `Recipe.vessels`
 *                       (scaled to nearest standard size from src/vessels.ts
 *                       based on the recipe multiplier)
 *   {vessel:key}      → VesselRef for another `Recipe.vessels` entry
 *                       e.g. {vessel:pot}
 *
 * Text between placeholders is preserved verbatim. Throws on malformed
 * placeholders so authoring errors fail loud at recipe-load time.
 */
export function parseBullet(s: string): DirectionSegment[] {
  const result: DirectionSegment[] = [];
  let last = 0;
  for (const m of s.matchAll(PLACEHOLDER_RE)) {
    if (m.index > last) result.push(s.slice(last, m.index));
    const inside = m[1];
    if (inside === undefined) throw new Error(`malformed placeholder near "${m[0]}"`);
    result.push(parseToken(inside));
    last = m.index + m[0].length;
  }
  if (last < s.length) result.push(s.slice(last));
  return result;
}

function parseToken(t: string): DirectionSegment {
  if (t.startsWith('!')) return parseTemp(t.slice(1), t);
  if (t.startsWith('=')) return parseScaledQty(t.slice(1).trim(), t);
  const colon = t.indexOf(':');
  if (t.trim() === 'vessel') return { vessel: true, key: MAIN_VESSEL };
  if (colon >= 0 && t.slice(0, colon).trim() === 'vessel') {
    return parseVesselRef(t.slice(colon + 1).trim(), t);
  }
  if (colon >= 0) {
    const name = t.slice(0, colon).trim();
    if (!name) throw new Error(`bad ref placeholder "{${t}}": empty name`);
    return parseRefWithPortion(name, t.slice(colon + 1).trim(), t);
  }
  const name = t.trim();
  if (!name) throw new Error('empty placeholder "{}"');
  return { ref: name };
}

function parseVesselRef(key: string, full: string): VesselRef {
  if (!key) throw new Error(`bad vessel placeholder "{${full}}": empty key`);
  return { vessel: true, key };
}

function parseTemp(body: string, full: string): Temperature {
  const m = body.match(TEMP_RE);
  if (!(m?.[1] && m[2])) {
    throw new Error(`bad temperature placeholder "{${full}}" (expected {!350F} or {!175C})`);
  }
  return { temp: Number(m[1]), unit: m[2] as 'F' | 'C' };
}

function parseScaledQty(body: string, full: string): ScaledQuantity {
  const fixed = body.startsWith('fixed ');
  const rest = fixed ? body.slice('fixed '.length).trim() : body;
  const m = rest.match(QTY_RE);
  if (!m?.[1]) throw new Error(`bad quantity placeholder "{${full}}"`);
  const out: ScaledQuantity = m[2] ? { quantity: m[1], unit: m[2] } : { quantity: m[1] };
  if (fixed) out.fixed = true;
  return out;
}

function parseRefWithPortion(name: string, spec: string, full: string): IngredientRef {
  if (spec === 'half' || spec === 'third') return { ref: name, portion: spec };
  const m = spec.match(QTY_RE);
  if (!m?.[1]) throw new Error(`bad portion in "{${full}}"`);
  const portion = m[2] ? { quantity: m[1], unit: m[2] } : { quantity: m[1] };
  return { ref: name, portion };
}
